import React from 'react';
import PropTypes from 'prop-types';

import Select, { components } from 'react-select';
import { ThemeContext } from 'styled-components';
import { FilledClose } from '@oca/icons';

export const BaseSelect = React.forwardRef(
  /**
   * @param {import('./base-select').BaseSelectProps<T>} props
   * @param {import('react').Ref} ref
   */
  function BaseSelect(props, ref) {
    const { styles, error, components: componentsProp, ...rest } = props;
    const theme = React.useContext(ThemeContext);

    return (
      <Select
        {...rest}
        ref={ref}
        menuPortalTarget={document.body}
        components={{
          IndicatorSeparator: null,
          MultiValueRemove,
          DropdownIndicator,
          ...componentsProp,
        }}
        styles={{
          input: base => ({
            ...base,
            color: theme.colors.text,
            fontSize: 14,
          }),
          placeholder: base => ({
            ...base,
            color: theme.colors.textSecondary,
            fontWeight: 400,
          }),
          control: (base, { isFocused }) => ({
            ...base,
            borderRadius: 8,
            backgroundColor: '#f8f9fa',
            border: 'none',
            boxShadow: 'none',
            paddingLeft: 8,
            ...(error
              ? {
                  borderColor: theme.colors.error,
                  ':hover': {
                    borderColor: theme.colors.error,
                  },
                }
              : {}),
            ...(isFocused && error
              ? { boxShadow: `0 0 0 1px ${theme.colors.error}` }
              : {}),
          }),
          menuPortal: base => ({ ...base, zIndex: 9999 }),
          menu: base => ({
            ...base,
            zIndex: 9999,
            borderRadius: 8,
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
            padding: 4,
          }),
          multiValue: base => ({
            ...base,
            padding: '4px 0 4px 4px',
            backgroundColor: theme.colors.primary,
            color: theme.colors.greys[400],
            borderRadius: 4,
            alignItems: 'center',
          }),
          multiValueLabel: base => ({
            ...base,
            color: theme.colors.greys.dark,
            fontSize: 12,
            padding: 0,
          }),
          multiValueRemove: base => {
            return {
              ...base,
              cursor: 'pointer',
              ':hover': { backgroundColor: 'none', opacity: 0.8 },
            };
          },
          singleValue: (base, { isDisabled }) => ({
            ...base,
            fontSize: 14,
            color: isDisabled ? theme.colors.textSecondary : theme.colors.text,
          }),
          valueContainer: (base, { isMulti }) => ({
            ...base,
            ...(isMulti ? { overflowY: 'auto', maxHeight: 150 } : {}),
          }),
          option: (base, { isSelected, isFocused, isDisabled }) => {
            let color = theme.colors.text;
            let backgroundColor =
              isSelected || isFocused ? theme.colors.greys[100] : 'none';
            if (isDisabled) {
              // eslint-disable-next-line prefer-destructuring
              color = theme.colors.greys[400];
              // eslint-disable-next-line prefer-destructuring
              backgroundColor = theme.colors.greys[100];
            }
            const hoverStyle = {
              backgroundColor:
                isSelected || isFocused || isDisabled
                  ? 'none'
                  : theme.colors.primary[100],
            };

            return {
              ...base,
              color,
              backgroundColor,
              fontSize: theme.fontSizes[1],
              '&:hover': hoverStyle,
              '&:active': hoverStyle,
              borderRadius: 8,
            };
          },
          ...styles,
        }}
      />
    );
  },
);

BaseSelect.propTypes = {
  // eslint-disable-next-line react/require-default-props
  styles: PropTypes.objectOf(PropTypes.func),
  // eslint-disable-next-line react/require-default-props
  components: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.func, PropTypes.node, PropTypes.object]),
  ),
  error: PropTypes.bool,
};

BaseSelect.defaultProps = {
  error: false,
};

function MultiValueRemove(props) {
  return (
    <components.MultiValueRemove {...props}>
      <FilledClose color="currentColor" fontSize={18} />
    </components.MultiValueRemove>
  );
}

function DropdownIndicator(props) {
  // eslint-disable-next-line react/prop-types
  const { selectProps } = props;
  const isMenuOpen = selectProps.menuIsOpen;

  return (
    <components.DropdownIndicator {...props}>
      {isMenuOpen ? (
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M14.8049 7.31623C15.1155 7.62688 15.1155 8.1305 14.8049 8.44115L10.5624 12.6836C10.2517 12.9942 9.74815 12.9942 9.4375 12.6836L5.19505 8.44115C4.8844 8.1305 4.8844 7.62688 5.19505 7.31623C5.5057 7.00559 6.00928 7.00559 6.31994 7.31623L9.99994 10.9962L13.6799 7.31623C13.9906 7.00559 14.4942 7.00559 14.8049 7.31623Z"
            fill="#64748B"
          />
        </svg>
      ) : (
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M14.8049 7.31623C15.1155 7.62688 15.1155 8.1305 14.8049 8.44115L10.5624 12.6836C10.2517 12.9942 9.74815 12.9942 9.4375 12.6836L5.19505 8.44115C4.8844 8.1305 4.8844 7.62688 5.19505 7.31623C5.5057 7.00559 6.00928 7.00559 6.31994 7.31623L9.99994 10.9962L13.6799 7.31623C13.9906 7.00559 14.4942 7.00559 14.8049 7.31623Z"
            fill="#64748B"
          />
        </svg>
      )}
    </components.DropdownIndicator>
  );
}
